<template>
  <v-main class="pt-0">
    <v-container class="maxContainerWidth mb-16">
      <!-- Make a Payment Header -->
      <v-row class="text-white mt-4 mb-4">
        <v-col class="d-flex align-center flex-wrap">
          <img
            src="../../PolicyMyPolicy/assets/ChangeBillingMethod.02503efe.svg"
            alt="Change Billing"
            width="48"
          />
          <h1 class="text-white pl-2">
            {{ $t('L_make_a_payment') }}
          </h1>
          <v-spacer></v-spacer>
          <!-- Policy Number -->
          <h3 class="text-white" :class="$vuetify.display.xs ? 'ml-10 pl-4' : 'ml-0 pl-0'">
            {{ $t('L_payment_policy_number') }}
            <span class="font-weight-regular">{{ getPolicyNum }}</span>
          </h3>
        </v-col>
      </v-row>

      <MessageDialog
        :show-dialog="dialog"
        :has-button-two="true"
        type="warning"
        :title="$t('L_cancel_payment_process')"
        :message="$t('L_do_you_want_to_cancel_the_current_payment_process_and_not_submit')"
        :button-two-label="$t('L_yes')"
        :button-one-label="$t('L_no')"
        :is-persistent="true"
        @buttonTwoAction="closeAndGoToPolicyDetails"
        @buttonOneAction="dialog = false"
      />
      <!-- Section Guide
        One Time Payment -    1 Payment Amount  2 Payment Method  3 Welcome Back
        ACH              -    1 Payment Amount  2 Payment Method  3 Review Payment 4 Confirmation
        -->
      <!-- ! This is for screen larger than xsOnly Horizontal Mode -->
      <v-stepper
        v-model="step"
        :vertical="$vuetify?.display?.xs"
        :class="{ 'pl-4 py-4': $vuetify.display.xs }"
      >
        <v-stepper-header v-show="!$vuetify?.display?.xs">
          <v-stepper-item :complete="step > 1" value="1">
            {{ $t('L_payment_amount') }}
          </v-stepper-item>
          <v-divider />
          <v-stepper-item :complete="step > 2" value="2">
            {{ $t('L_payment_method') }}
          </v-stepper-item>
          <v-divider />
          <v-stepper-item :complete="step > 3" value="3">
            {{ $t(parseInt(selectedMethod) === 2 ? 'L_welcome_back' : 'L_review_payment') }}
          </v-stepper-item>
          <v-divider v-show="parseInt(selectedMethod) !== 2" />
          <v-stepper-item v-show="parseInt(selectedMethod) !== 2" :complete="step > 4" value="4">
            {{ $t('L_confirmation') }}
          </v-stepper-item>
        </v-stepper-header>

        <v-stepper-window class="my-0 mx-0">
          <v-stepper-item
            :complete="step > 1"
            value="1"
            v-show="$vuetify?.display?.xs"
            class="pa-0 mx-0 mt-2 mb-2"
            :class="{
              'my-step-complete': step === '1',
              'border-complete': $vuetify.display.xs && step > 1,
            }"
          >
            <span class="pl-1">{{ $t('L_payment_amount') }}</span>
          </v-stepper-item>
          <v-stepper-window-item
            value="1"
            :class="{ 'border-s-thin border-opacity ml-3': $vuetify.display.xs }"
          >
            <PayStepOne
              @closeAndGoToPolicyDetails="closeAndGoToPolicyDetails"
              @cancelDialog="dialog = true"
              @minSelected="selectedPaymentId = 0"
              @totalSelected="selectedPaymentId = 1"
              @goToStepTwo="goToStepTwo"
            />
          </v-stepper-window-item>

          <v-stepper-item
            :complete="step > 2"
            value="2"
            v-show="$vuetify?.display?.xs"
            class="pa-0 mx-0 mt-2 mb-2"
            :class="{
              'my-step-complete': step === '2',
              'border-disabled': $vuetify.display.xs && step === '1',
              'border-complete': $vuetify.display.xs && step > 2,
            }"
          >
            <span class="pl-1">{{ $t('L_payment_method') }}</span>
          </v-stepper-item>
          <v-stepper-window-item
            value="2"
            :class="{ 'border-s-thin border-opacity ml-3': $vuetify.display.xs }"
          >
            <PayStepTwo
              :policy-data="currentPolicy"
              :minimum-amount-selected="selectedPaymentId == 0"
              :selected-amount="selectedAmount"
              @cancelDialog="dialog = true"
              @goToStepThree="goToStepThree"
              @goToStepThreeCC="gotoStepThreeCC"
              @paymentMethodSet="handlePaymentMethodSet"
            />
          </v-stepper-window-item>

          <v-stepper-item
            :complete="step > 3"
            value="3"
            v-show="$vuetify?.display?.xs"
            class="pa-0 mx-0 mt-2 mb-2"
            :class="{
              'my-step-complete': step === '3',
              'border-disabled':
                $vuetify.display.xs &&
                (step === '1' || (step === '2' && parseInt(selectedMethod) !== 2)),
              'border-complete': $vuetify.display.xs && step > 3,
            }"
          >
            <span class="pl-1">{{
              $t(parseInt(selectedMethod) === 2 ? 'L_welcome_back' : 'L_review_payment')
            }}</span>
          </v-stepper-item>
          <v-stepper-window-item
            value="3"
            :class="{ 'border-s-thin border-opacity ml-3': $vuetify.display.xs }"
          >
            <div v-show="isKubra">
              <!-- KUBRA -->
              <PayStepThreeKubra
                @cancelDialog="dialog = true"
                @close-external-kubra="closeExternalKubraWindow"
              />
            </div>
            <!-- ACH -->
            <div v-show="!isKubra">
              <PayStepThreeACH
                @goBackToPaymentMethod="goBackToStepTwo"
                @goBackToPaymentAmount="goBackToStepOne"
                @cancelDialog="dialog = true"
                @goToStepFour="goToStepFour"
                :selected-amount="selectedAmount"
              />
            </div>
          </v-stepper-window-item>

          <v-stepper-item
            :complete="step > 4"
            value="4"
            v-show="$vuetify?.display?.xs && parseInt(selectedMethod) !== 2"
            class="pa-0 mx-0 mt-2 mb-2"
            :class="{
              'my-step-complete': step === '4',
            }"
          >
            <span class="pl-1">{{ $t('L_confirmation') }}</span>
          </v-stepper-item>
          <v-stepper-window-item
            value="4"
            v-show="isKubra === false"
            :class="{ 'border-s-thin border-opacity ml-3': $vuetify.display.xs }"
          >
            <PayStepFour @cancelDialog="dialog = true" @goToStepFive="goToStepFive" />
          </v-stepper-window-item>
        </v-stepper-window>
      </v-stepper>
    </v-container>
    <FooterComponent />
    <NavigationConfirmationDialog
      v-if="showNavigationConfirmationDialog"
      :show-dialog="showNavigationConfirmationDialog"
      @confirm-navigation="navigateToRoute"
      @cancel-navigation="showNavigationConfirmationDialog = false"
    />
  </v-main>
</template>
<script lang="ts">
import { defineComponent, type ComponentOptions } from 'vue';
import { currency } from '@/shared/util/formatters';
import MessageDialog from '@/components/MessageDialog.vue';
import PayStepOne from './components/StepOne/PayStepOne.vue';
import PayStepTwo from './components/StepTwo/PayStepTwo.vue';
import PayStepThreeACH from './components/StepThree/PayStepThreeACH.vue';
import PayStepThreeKubra from './components/StepThree/PayStepThreeKubra.vue';
import PayStepFour from './components/StepFour/PayStepFour.vue';
import { mapActions, mapState, mapStores } from 'pinia';
import { useCustomerStore } from '@/stores/customer/customer.store';
import { usePolicyStore } from '@/modules/policy/store/policy.store';
import { useGlobalStore } from '@/stores/global/global.store';
import { RouteNames } from '@/router/routeNames';
import FooterComponent from '@/components/Footer/Footer.vue';
import NavigationConfirmationDialog from '@/components/NavigationConfirmation/NavigationConfirmationDialog.vue';
import type { RouteParamsGeneric } from 'vue-router';

// Import the IPolicyStorePaymentData type

const componentOptions: ComponentOptions = {
  name: 'PaymentMethodLayout',
  components: {
    PayStepOne,
    PayStepTwo,
    PayStepThreeACH,
    PayStepThreeKubra,
    PayStepFour,
    MessageDialog,
    FooterComponent,
    NavigationConfirmationDialog,
  },
  data: () => ({
    persistent: true,
    dialog: false,
    step: 1,
    isKubra: false,
    selectedAmount: '',
    selectedMethod: '',
    accountNumber: '',
    routingNumber: '',
    selectedPaymentId: -1,
    curPaymentData: {
      amount: '',
      paymentType: '',
      accountType: '',
      accountCode: '',
      accountNumber: '',
      routingNumber: '',
      method: '',
      // Add any other required fields from IPolicyStorePaymentData here
    },
    kubraExternalWindow: null as Window | null,
    showNavigationConfirmationDialog: false,
    navigatingToRoute: '',
    navigatingToParams: {} as RouteParamsGeneric,
    navigationAwayConfirmed: false,
  }),
  computed: {
    ...mapStores(usePolicyStore),
    ...mapState(useCustomerStore, ['customerProfile']),
    ...mapState(usePolicyStore, ['currentPolicy', 'selectedPolicyIndex', 'paymentData']),
    ...mapState(useGlobalStore, {
      locale: 'getLocale',
    }),
    getTotalDue() {
      return currency(+this.currentPolicy?.paynowInfo?.totalPaymentAmount, 2);
    },
    getMinimumDue() {
      return currency(+this.currentPolicy?.paynowInfo?.minimumPaymentAmount, 2);
    },
    getPolicyNum() {
      return this.currentPolicy?.contractId || '';
    },
  },
  methods: {
    ...mapActions(useGlobalStore, {
      removeAlerts: 'removeAlerts',
    }),
    navigateToRoute() {
      this.navigationAwayConfirmed = true;
      this.$router.push({
        name: this.navigatingToRoute,
        params: this.navigatingToParams,
      });
    },
    handlePaymentMethodSet(method: string) {
      this.selectedMethod = method;
    },
    goBackToStepOne() {
      this.selectedPaymentId = -1;
      this.selectedAmount = '';
      this.step = '1';
    },
    goBackToStepTwo() {
      this.selectedMethod = '';
      this.accountNumber = '';
      this.routingNumber = '';
      this.step = '2';
    },
    async closeAndGoToPolicyDetails() {
      // Clear Bank
      this.policyStore.bankName = null;
      // Clear out the saved data for payment in the store
      this.policyStore.paymentData.accountCode = '';
      this.policyStore.paymentData.accountNumber = '';
      this.policyStore.paymentData.routingNumber = '';
      this.policyStore.paymentData.accountType = '';
      this.policyStore.paymentData.bankName = '';

      this.navigationAwayConfirmed = true;
      this.policyStore.paymentStep = -1;
      await this.$router.push({
        name: RouteNames.POLICY_DETAILS,
        params: { contractId: this.selectedPolicyIndex },
      });
    },
    goToStepTwo(amount: string) {
      this.selectedAmount = amount.replaceAll('$', '');
      this.step = '2';
    },
    goToStepThree() {
      // This is called from PayStepTwo
      // this.$store.commit('policy/setPaymentData', paymentData);
      this.curPaymentData = this.paymentData;
      this.isKubra = false;
      this.step = '3';
    },
    gotoStepThreeCC(winObj: Window) {
      this.isKubra = true;
      this.step = '3';
      this.kubraExternalWindow = winObj;
      // Launch the Kubra site on new tab
      // Kubra Done Page can navigate away
      this.policyStore.paymentStep = 3;
    },
    goToStepFour() {
      this.step = '4';
      // ACH Done Page can navigate away
      this.policyStore.paymentStep = 3;
    },
    goToStepFive() {
      this.step = '5';
      this.closeAndGoToPolicyDetails();
    },
    closeExternalKubraWindow() {
      this.kubraExternalWindow.close();
    },
  },
  watch: {
    step(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.removeAlerts('payment');
      }

      // * Dynatrace Tag (https://primerica.atlassian.net/wiki/spaces/PD/pages/19955873/Payments+-+Custom+Dynatrace+Tags)
      const step = newVal;
      if (step === '1') {
        // Payment Selection page displayed
        try {
          const i = this.$dynatrace.enterAction(
            `DisplayedPayments: Total $${this.getTotalDue}, Minimum $${this.getMinimumDue})`
          );
          this.$dynatrace.leaveAction(i);
        } catch (_error) {
          /* empty */
        }
        try {
          const i = this.$dynatrace.enterAction('OneTimePayment_PaymentSelection_PageDisplayed');
          this.$dynatrace.leaveAction(i);
        } catch (_error) {
          /* empty */
        }
      } else if (step === '2') {
        // The Payment Method page is displayed.
        try {
          const i = this.$dynatrace.enterAction('OneTimePayment_PaymentMethod_PageDisplayed');
          this.$dynatrace.leaveAction(i);
        } catch (_error) {
          /* empty */
        }
      } else if (step === '3') {
        // The Payment Review page is displayed.
        try {
          const i = this.$dynatrace.enterAction(
            'OneTimePayment_ACH_PaymentAgreement_PageDisplayed'
          );
          this.$dynatrace.leaveAction(i);
        } catch (_error) {
          /* empty */
        }
      } else if (step === '4') {
        try {
          // The Payment Confirmation page is displayed.
          const i = this.$dynatrace.enterAction(
            'OneTimePayment_ACH_PaymentConfirmation_PageDisplayed'
          );
          this.$dynatrace.leaveAction(i);
          const j = this.$dynatrace.enterAction('OneTimePayment_submit');
          this.$dynatrace.leaveAction(j);
        } catch (_error) {
          /* empty */
        }
      }
    },
  },
  beforeRouteLeave(to, _, next) {
    const isProcessDone = this.policyStore.paymentStep === -1 || this.navigationAwayConfirmed;
    // const navigatingOutsideMinorAccountLink =
    //   to.name !== RouteNames.MINOR_ACCOUNT_LINK_PROCESS &&
    //   to.path !== '/minor-account-link-process';
    // navigatingOutsideMinorAccountLink &&
    if (!isProcessDone) {
      this.showNavigationConfirmationDialog = true; // show NavigationConfirmationDialog
      this.navigatingToRoute = to.name as string; // save the route name
      this.navigatingToParams = to.params;
      next(false); // cancel the navigation
    } else {
      this.policyStore.paymentStep = -1; // reset the step when navigation happens
      next();
    }
  },
};

export default defineComponent(componentOptions);
</script>

<style lang="scss" scoped>
.maxContainerWidth {
  max-width: 1024px !important;
  margin: auto;
}

.dollarSignBig {
  font-size: 36px;
}

.dollarSignSmall {
  font-size: 28px;
}

.dollarSignIconBig {
  font-size: 56px;
}

.dollarSignIconSmall {
  font-size: 36px;
}

@media only screen and (max-width: 959px) {
  .v-stepper:not(.v-stepper--vertical) .v-stepper__label {
    display: block !important;
    padding-left: 5px;
  }
}

.v-stepper__header {
  overflow: auto;
  display: flex !important;
  flex-wrap: no-wrap;
  justify-content: left;
}

.border-disabled[disabled].v-stepper-item {
  position: relative;
  padding-bottom: 8px !important;
}
.border-disabled[disabled].v-stepper-item::before,
.border-complete.v-stepper-item--complete::before {
  content: '|';
  color: rgba(var(--v-theme-black));
  opacity: 0.12;
  position: absolute;
  padding-top: 8px;
  left: 8px;
  top: 15px;
}
</style>
